import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config/environment';
import managementService from '../../services/managementService';
import { companyData } from '../../data/scorecardData';
import ScorecardNavigation from './ScorecardNavigation';
import ScorecardDropdown, { type DropdownOption } from '../common/ScorecardDropdown';
import * as Sentry from '@sentry/react';

interface Server {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  location_id: string;
  created: string;
}

interface Location {
  id: string;
  name: string;
  created: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [servers, setServers] = useState<Server[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStore, setSelectedStore] = useState<string>('');

  // Get store locations from company data (fallback for dev mode)
  const fallbackStoreLocations = companyData.storeLocations;

  // Determine if user is admin
  const isAdmin = useMemo(() => {
    return user?.managementUser?.role === 'admin';
  }, [user?.managementUser?.role]);

  // Get user's location for store managers
  const userLocationId = useMemo(() => {
    return user?.managementUser?.location_id;
  }, [user?.managementUser?.location_id]);

  // Fetch locations from database
  useEffect(() => {
    const fetchLocations = async () => {
      if (config.useDummyData) {
        // In dev mode, use fallback data
        return;
      }
      
      setIsLoadingLocations(true);
      try {
        const response = await managementService.getLocations();
        if (response.success && response.locations) {
          setLocations(response.locations);
        } else {
          console.error('Failed to fetch locations:', response.error);
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        Sentry.captureException(err);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Get available locations (database or fallback)
  const availableLocations = useMemo(() => {
    if (config.useDummyData || locations.length === 0) {
      // Use fallback data in dev mode or when database is not available
      return fallbackStoreLocations.map(name => ({ id: name, name, created: '' }));
    }
    return locations;
  }, [locations, fallbackStoreLocations]);

  // Set initial store selection
  useEffect(() => {
    if (availableLocations.length > 0) {
      if (isAdmin) {
        // For admins, default to first store
        setSelectedStore(availableLocations[0].id);
      } else if (userLocationId) {
        // For store managers, use their location
        setSelectedStore(userLocationId);
      }
    }
  }, [isAdmin, availableLocations, userLocationId]);

  // Fetch servers when store selection changes
  useEffect(() => {
    if (selectedStore) {
      fetchServers();
    }
  }, [selectedStore]);

  const fetchServers = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use location ID for database queries, or fallback to store name for dev mode
      const locationFilter = config.useDummyData ? selectedStore : selectedStore;
      const response = await managementService.getServers(locationFilter);
      
      if (response.success && response.servers) {
        setServers(response.servers);
      } else {
        setError(response.error || 'Failed to fetch servers');
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
      Sentry.captureException(err);
      setError('Failed to fetch servers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoreChange = (storeId: string) => {
    setSelectedStore(storeId);
  };

  // Convert locations to dropdown options
  const storeOptions: DropdownOption[] = useMemo(() => {
    return availableLocations.map(location => ({
      value: location.id,
      label: location.name
    }));
  }, [availableLocations]);

  const getStoreName = (storeId: string) => {
    const location = availableLocations.find(loc => loc.id === storeId);
    return location ? location.name : 'Unknown Store';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Scorecard Navigation */}
      <ScorecardNavigation />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Store:</span>
              {isAdmin ? (
                <ScorecardDropdown
                  value={selectedStore}
                  onChange={handleStoreChange}
                  options={storeOptions}
                  loading={isLoadingLocations}
                  loadingText="Loading locations..."
                  variant="inline"
                />
              ) : (
                <span className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md">
                  {isLoadingLocations ? 'Loading...' : getStoreName(selectedStore)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-2 text-gray-600">Loading servers...</span>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Servers ({servers.length})
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                List of servers for {getStoreName(selectedStore)}
              </p>
            </div>
            
            {servers.length === 0 ? (
              <div className="px-4 py-5 sm:px-6 text-center text-gray-500">
                No servers found for this store.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        First Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {servers.map((server) => (
                      <tr key={server.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {server.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {server.first_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {server.last_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(server.created)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
